import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { updateReadReport } from '../../../../states/containers/report/reportSlice'
import { endOfMonth, endOfWeek, format, nowDate } from '../../../../utils/date'
import { setPath, setUrlParam } from '../../../../utils/url'
import { URL } from '../../../constants/url'

type LangValue = 'ja' | 'en'

interface Props {
  target: string
  id: number
  year: string
  month: string
  list: {
    date: string
    total_evaluation: string
    weekly_reports: {
      date: string
      is_read: boolean
    }[]
    is_read: boolean
  } | undefined
}

const CalendarView: FC<Props> = (props) => {
  const {
    target,
    id,
    year,
    month,
    list,
  } = props

  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()

  const { language } = useParams<{ language: LangValue }>()

  const [isDisplayMenu, setIsDisplayMenu] = useState<boolean>(false)

  const toggleMenu = () => {
    setIsDisplayMenu(!isDisplayMenu)
  }

  const bg_color = useMemo(() => {
    if (!list) return "#FFF"
    if (list.total_evaluation === 'Excellent') return "#446BF4"
    if (list.total_evaluation === 'Great') return "#44B5F4"
    if (list.total_evaluation === 'Good') return "#4C5B7B"
    if (list.total_evaluation === 'Fair') return "#446BF4"
    return "#FFF"
  }, [list])

  const border_color = useMemo(() => {
    if (!list) return "#EEE"
    if (list.total_evaluation === 'Excellent') return "#8EA6F4"
    if (list.total_evaluation === 'Great') return "#8FCFF5"
    if (list.total_evaluation === 'Good') return "#939CB1"
    if (list.total_evaluation === 'Fair') return "#8EA6F4"
    return "#EEE"
  }, [list])

  const is_read = useMemo(() => {
    if (!list) return true
    if (list.total_evaluation) {
      return list.is_read && !(list.weekly_reports.filter((weekly_report) => weekly_report.is_read === false).length)
    }
    return !(list.weekly_reports.filter((weekly_report) => weekly_report.is_read === false).length)
  }, [list])

  const clickLink = (range: string, date?: string) => {
    let targetDate = `${year}-${`00${month}`.slice(-2)}-01`
    if (date) {
      targetDate = date
    }
    history.push(`${setPath(setUrlParam(
      URL.REPORT_DETAIL.path,
      [
        { key: 'range', value: range },
        { key: 'target', value: target },
        { key: 'id', value: id.toString() },
      ],
    ), language)}?date=${targetDate}`)
    dispatch(updateReadReport({ id: id, target: target, type: range, date: targetDate }))
  }

  const progress = useMemo(() => (Number(format(nowDate(), "D")) / (Number(endOfMonth(nowDate(), "D")) + 1)) * 100, [])

  return (
    <div className={`card_detail ${(!list && Number(`${year}${`00${month}`.slice(-2)}`) !== Number(nowDate('YYYYMM'))) ? "opa_0_5" : ""}`}>
      <div className="card_wrap">
        <div className="card_title">
          {year}
          /
          {`00${month}`.slice(-2)}
          {
            is_read ? (
              <></>
            ) : (
              <div className="new">
                New
              </div>
            )
          }
        </div>
        <div className="graph_wrap">
          {
            list && list.total_evaluation && (
              <div
                data-gtm-click={`${t('report.list.box.monthly_report')} - button`}
                className="created"
                style={{
                  backgroundColor: bg_color,
                  border: `solid 16px ${border_color}`,
                }}
                role="none"
                onClick={() => clickLink('monthly')}
              >
                {list.total_evaluation}
              </div>
            )
          }
          {
            (
              Number(`${year}${`00${month}`.slice(-2)}`) === Number(nowDate('YYYYMM'))
            ) && (
              <div
                className="making"
                style={{
                  backgroundColor: bg_color,
                  backgroundImage:
                  `radial-gradient(#FFFFFF 50%, transparent 50%),
                  conic-gradient(#4472BA 0% ${progress}%,
                  #EAEBEB ${progress}%, #EAEBEB 100%)`,
                }}
              >
                <div>
                  {t('report.list.box.making')}
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    color: "#4472BA",
                  }}
                >
                  {Math.floor(progress)}
                  %
                </div>
              </div>
            )
          }
          {
            (
              (list && !list.total_evaluation && Number(`${year}${`00${month}`.slice(-2)}`) < Number(nowDate('YYYYMM')))
              || (!list && Number(`${year}${`00${month}`.slice(-2)}`) !== Number(nowDate('YYYYMM')))
            ) && (
              <div className="not_created">
                {t('report.list.box.not_created')}
              </div>
            )
          }
        </div>
        {
          list ? (
            <div
              data-gtm-click={`${t('report.list.box.view_report')} - button`}
              className="view_report"
              onClick={() => toggleMenu()}
              role="none"
            >
              <div>
                {t('report.list.box.view_report')}
              </div>
              <FontAwesomeIcon icon={['fas', 'angle-down']} />
            </div>
          ) : (
            <div className="view_report not">
              <div>
                {t('report.list.box.view_report')}
              </div>
              <FontAwesomeIcon icon={['fas', 'angle-down']} />
            </div>
          )
        }
        <div className={`report_list ${isDisplayMenu || "hidden"}`}>
          {
            list && list.total_evaluation && (
              <>
                <div className="title">
                  {t('report.list.box.monthly_report')}
                </div>
                <div
                  data-gtm-click={`${t('report.list.box.monthly_report')} - link`}
                  className="report_list_row"
                  role="none"
                  onClick={() => clickLink('monthly')}
                >
                  <div className="text">
                    <div>
                      {format(list.date, "M月度")}
                      {t('report.list.box.monthly_report')}
                    </div>
                    {
                      list.is_read ? (
                        <></>
                      ) : (
                        <div className="new">
                          New
                        </div>
                      )
                    }
                  </div>
                  <div className="icon">
                    <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                  </div>
                </div>
              </>
            )
          }
          {
            list && list.weekly_reports && (
              <div className="title">
                週間業務レポート
              </div>
            )
          }
          {
            list && list.weekly_reports.map((weekly_report) => (
              <div
                data-gtm-click={`${t('report.list.box.weekly_report')} - link`}
                className="report_list_row"
                key={`weekly_report_${weekly_report.date}`}
                role="none"
                onClick={() => clickLink('weekly', weekly_report.date)}
              >
                <div className="text">
                  <div>
                    {format(weekly_report.date, "M/D")}
                    ~
                    {format(endOfWeek(weekly_report.date), "M/D")}
                  </div>
                  {
                    weekly_report.is_read ? (
                      <></>
                    ) : (
                      <div className="new">
                        New
                      </div>
                    )
                  }
                </div>
                <div className="icon">
                  <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                </div>
              </div>
            ))
          }
        </div>
        <div className={`close_screen gray ${isDisplayMenu || 'none'}`} role="presentation" onClick={() => setIsDisplayMenu(false)} />
      </div>
    </div>
  )
}

export default CalendarView
